import { useEffect } from "react";

type Surface = {
  element: HTMLElement;
  body: HTMLElement | null;
  header: HTMLElement | null;
  baseWidth: number;
  x: number;
  y: number;
};

const GUTTER = 16;
const INTERACTIVE =
  "button, a, input, textarea, select, [role='button'], [contenteditable='true'], .ant-select, .ant-picker";
const MODAL_HANDLE =
  ".ant-modal-header, .ant-modal-confirm-title, .erp-locator__head, .erp-journey-modal__hero";

function isDrawer(element: HTMLElement) {
  return element.classList.contains("ant-drawer");
}

function surfaceElement(root: HTMLElement): HTMLElement | null {
  return isDrawer(root)
    ? root.querySelector<HTMLElement>(".ant-drawer-content-wrapper")
    : root;
}

function columnWidth(column: HTMLTableColElement): number {
  const raw = column.style.width || column.getAttribute("width") || "";
  const parsed = Number.parseFloat(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 120;
}

function fitTables(root: HTMLElement): number {
  let widest = 0;
  root
    .querySelectorAll<HTMLElement>(".ant-table-wrapper")
    .forEach((wrapper) => {
      let tableWidth = 0;
      wrapper
        .querySelectorAll<HTMLTableElement>(".ant-table-container table")
        .forEach((table) => {
          const labels = Array.from(
            table.querySelectorAll("thead tr:last-child th"),
            (cell) => cell.textContent?.trim() || "明细",
          );
          table.querySelectorAll("tbody > tr").forEach((row) => {
            Array.from(row.children).forEach((cell, index) => {
              if (
                !(cell instanceof HTMLElement) ||
                cell.hasAttribute("colspan")
              )
                return;
              const label = labels[index] || "明细";
              if (cell.dataset.erpDialogLabel !== label)
                cell.dataset.erpDialogLabel = label;
            });
          });
          const columns = Array.from(
            table.querySelectorAll<HTMLTableColElement>("colgroup > col"),
          );
          const widths = columns.map(columnWidth);
          const leafHeaders = table.tHead?.rows.item(
            table.tHead.rows.length - 1,
          );
          const leafCount = leafHeaders
            ? Array.from(leafHeaders.cells).reduce(
                (count, cell) => count + cell.colSpan,
                0,
              )
            : 0;
          // Ant leaves trailing columns without an explicit width out of colgroup.
          // Reserve room for them, or the final cell collapses to a few pixels.
          const missing = Math.max(0, leafCount - columns.length);
          const total =
            widths.reduce((sum, width) => sum + width, 0) + missing * 150;
          if (columns.length > 0) {
            tableWidth = Math.max(tableWidth, total);
            columns.forEach((column, index) => {
              const share = `${((widths[index] ?? 120) / total) * 100}%`;
              if (
                column.style.getPropertyValue("--erp-dialog-column-share") !==
                share
              ) {
                column.style.setProperty("--erp-dialog-column-share", share);
              }
            });
          } else {
            tableWidth = Math.max(tableWidth, table.scrollWidth);
          }
        });
      widest = Math.max(widest, tableWidth);
    });
  return widest;
}

function clampOffset(surface: Surface, win: Window) {
  const node = surfaceElement(surface.element);
  if (!node) return;
  const rect = node.getBoundingClientRect();
  surface.x += Math.max(
    GUTTER - rect.left,
    Math.min(0, win.innerWidth - GUTTER - rect.right),
  );
  surface.y += Math.max(
    GUTTER - rect.top,
    Math.min(0, win.innerHeight - GUTTER - rect.bottom),
  );
  node.style.setProperty("--erp-dialog-drag-x", `${surface.x}px`);
  node.style.setProperty("--erp-dialog-drag-y", `${surface.y}px`);
}

function fitSurface(surface: Surface, win: Window) {
  const node = surfaceElement(surface.element);
  if (!node) return;
  const tableWidth = fitTables(surface.element);
  const body = surface.body;
  const bodyStyle = body ? win.getComputedStyle(body) : null;
  const bodyPadding = bodyStyle
    ? Number.parseFloat(bodyStyle.paddingLeft || "0") +
      Number.parseFloat(bodyStyle.paddingRight || "0")
    : 44;
  const overflow = body ? Math.max(0, body.scrollWidth - body.clientWidth) : 0;
  // The column widths are the useful minimum. At the viewport edge the table
  // shares the available width and wraps its cells instead of creating a rail.
  const desired = Math.max(
    surface.baseWidth,
    tableWidth ? tableWidth + bodyPadding + 24 : 0,
    overflow ? node.getBoundingClientRect().width + overflow : 0,
  );
  const width = Math.max(0, Math.min(desired, win.innerWidth - GUTTER * 2));
  const next = `${width}px`;
  if (node.style.getPropertyValue("--erp-dialog-width") !== next) {
    node.style.setProperty("--erp-dialog-width", next);
  }
  clampOffset(surface, win);
}

function isOpen(root: HTMLElement) {
  if (isDrawer(root)) return root.classList.contains("ant-drawer-open");
  return (
    !root.classList.contains("ant-modal-hidden") &&
    !root.className.includes("ant-zoom-leave") &&
    root.closest<HTMLElement>(".ant-modal-wrap")?.style.display !== "none"
  );
}

export function installErpDialogBehavior(doc: Document, win: Window) {
  const surfaces = new Map<HTMLElement, Surface>();
  let frame = 0;
  let drag: {
    surface: Surface;
    pointerId: number;
    startX: number;
    startY: number;
    x: number;
    y: number;
    rect: DOMRect;
  } | null = null;

  function sync() {
    frame = 0;
    const open = new Set<HTMLElement>();
    doc
      .querySelectorAll<HTMLElement>(".ant-modal, .ant-drawer")
      .forEach((root) => {
        if (!isOpen(root)) return;
        const node = surfaceElement(root);
        if (!node) return;
        open.add(root);
        let surface = surfaces.get(root);
        if (!surface) {
          const width = Number.parseFloat(node.style.width);
          surface = {
            element: root,
            body: root.querySelector<HTMLElement>(
              isDrawer(root) ? ".ant-drawer-body" : ".ant-modal-body",
            ),
            header: root.querySelector<HTMLElement>(
              isDrawer(root) ? ".ant-drawer-header" : MODAL_HANDLE,
            ),
            baseWidth:
              Number.isFinite(width) && width > 0
                ? width
                : node.getBoundingClientRect().width,
            x: 0,
            y: 0,
          };
          surfaces.set(root, surface);
          root.dataset.erpDialog = "";
        }
        surface.body = root.querySelector<HTMLElement>(
          isDrawer(root) ? ".ant-drawer-body" : ".ant-modal-body",
        );
        const header = root.querySelector<HTMLElement>(
          isDrawer(root) ? ".ant-drawer-header" : MODAL_HANDLE,
        );
        if (surface.header !== header) {
          surface.header?.removeAttribute("data-erp-dialog-handle");
          surface.header?.removeAttribute("tabindex");
          surface.header = header;
        }
        if (header) {
          header.dataset.erpDialogHandle = "";
          header.tabIndex = 0;
          header.setAttribute(
            "aria-label",
            `${header.textContent?.trim() || "弹窗"}，拖动可移动；方向键可微调位置`,
          );
        }
        fitSurface(surface, win);
      });
    surfaces.forEach((surface, root) => {
      if (open.has(root)) return;
      if (drag?.surface === surface) drag = null;
      surface.header?.removeAttribute("data-erp-dialog-handle");
      surface.header?.removeAttribute("tabindex");
      surface.header?.removeAttribute("aria-label");
      root.removeAttribute("data-erp-dialog");
      const node = surfaceElement(root);
      node?.style.removeProperty("--erp-dialog-width");
      node?.style.removeProperty("--erp-dialog-drag-x");
      node?.style.removeProperty("--erp-dialog-drag-y");
      surfaces.delete(root);
    });
  }

  function schedule() {
    if (!frame) frame = win.requestAnimationFrame(sync);
  }

  function move(event: PointerEvent) {
    if (!drag || event.pointerId !== drag.pointerId) return;
    const { surface, rect } = drag;
    const nextX = drag.x + event.clientX - drag.startX;
    const nextY = drag.y + event.clientY - drag.startY;
    surface.x = Math.max(
      drag.x + GUTTER - rect.left,
      Math.min(nextX, drag.x + win.innerWidth - GUTTER - rect.right),
    );
    surface.y = Math.max(
      drag.y + GUTTER - rect.top,
      Math.min(nextY, drag.y + win.innerHeight - GUTTER - rect.bottom),
    );
    const node = surfaceElement(surface.element);
    node?.style.setProperty("--erp-dialog-drag-x", `${surface.x}px`);
    node?.style.setProperty("--erp-dialog-drag-y", `${surface.y}px`);
  }

  function pointerDown(event: PointerEvent) {
    if (event.button !== 0 || win.innerWidth <= 720) return;
    const target = event.target;
    if (!(target instanceof Element) || target.closest(INTERACTIVE)) return;
    const header = target.closest<HTMLElement>("[data-erp-dialog-handle]");
    const root = header?.closest<HTMLElement>("[data-erp-dialog]");
    const surface = root && surfaces.get(root);
    const node = surface && surfaceElement(root);
    if (!surface || !node || !header) return;
    drag = {
      surface,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      x: surface.x,
      y: surface.y,
      rect: node.getBoundingClientRect(),
    };
    doc.body.classList.add("erp-dialog-dragging");
    event.preventDefault();
  }

  function stopDrag() {
    drag = null;
    doc.body.classList.remove("erp-dialog-dragging");
  }

  function keyDown(event: KeyboardEvent) {
    const header = event.target;
    if (
      !(header instanceof HTMLElement) ||
      !header.matches("[data-erp-dialog-handle]")
    )
      return;
    const root = header.closest<HTMLElement>("[data-erp-dialog]");
    const surface = root && surfaces.get(root);
    if (!surface) return;
    if (event.key === "Home") {
      surface.x = 0;
      surface.y = 0;
    } else if (event.key.startsWith("Arrow")) {
      const step = event.shiftKey ? 40 : 10;
      surface.x +=
        event.key === "ArrowRight"
          ? step
          : event.key === "ArrowLeft"
            ? -step
            : 0;
      surface.y +=
        event.key === "ArrowDown" ? step : event.key === "ArrowUp" ? -step : 0;
    } else return;
    event.preventDefault();
    const node = surfaceElement(surface.element);
    node?.style.setProperty("--erp-dialog-drag-x", `${surface.x}px`);
    node?.style.setProperty("--erp-dialog-drag-y", `${surface.y}px`);
    clampOffset(surface, win);
  }

  const observer = new MutationObserver(schedule);
  observer.observe(doc.body, {
    childList: true,
    attributes: true,
    attributeFilter: ["class"],
    subtree: true,
  });
  doc.addEventListener("pointerdown", pointerDown);
  doc.addEventListener("pointermove", move);
  doc.addEventListener("pointerup", stopDrag);
  doc.addEventListener("pointercancel", stopDrag);
  doc.addEventListener("keydown", keyDown);
  win.addEventListener("resize", schedule);
  win.addEventListener("blur", stopDrag);
  schedule();
  return () => {
    observer.disconnect();
    if (frame) win.cancelAnimationFrame(frame);
    doc.removeEventListener("pointerdown", pointerDown);
    doc.removeEventListener("pointermove", move);
    doc.removeEventListener("pointerup", stopDrag);
    doc.removeEventListener("pointercancel", stopDrag);
    doc.removeEventListener("keydown", keyDown);
    win.removeEventListener("resize", schedule);
    win.removeEventListener("blur", stopDrag);
    stopDrag();
    surfaces.forEach((surface) => {
      surface.element.removeAttribute("data-erp-dialog");
      surface.header?.removeAttribute("data-erp-dialog-handle");
      surface.header?.removeAttribute("tabindex");
      surface.header?.removeAttribute("aria-label");
      const node = surfaceElement(surface.element);
      node?.style.removeProperty("--erp-dialog-width");
      node?.style.removeProperty("--erp-dialog-drag-x");
      node?.style.removeProperty("--erp-dialog-drag-y");
      surface.element
        .querySelectorAll<HTMLTableColElement>("colgroup > col")
        .forEach((col) =>
          col.style.removeProperty("--erp-dialog-column-share"),
        );
    });
  };
}

export function ErpDialogBehavior() {
  useEffect(() => installErpDialogBehavior(document, window), []);
  return null;
}
